// C:/Repository/propsavvy-app/frontend/src/pages/dashboard/UnitDetails/UnitDetailsPage.tsx
import { getUnitRequest } from '@/api';
import Button from '@/components/shared/Button';
import Card from '@/components/shared/Card';
import PropertyOverview from '@/components/dashboard/Property/PropertyOverview';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { profileSelector } from '@/store/profile/profileSlices';
import { IUnit, PaymentMethod, PaymentPeriod } from '@/types';
import getAxiosErrors from '@/utils/getAxiosErrors';
import './UnitDetailsPage.scss';
import { useCollapsibleSections } from '@/hooks/useCollapsibleSections';
import { useToast } from '@/components/shared/ToastProvider';
import UnitInformation from '@/components/dashboard/UnitDetails/UnitInformation';
import UnitInclusions from '@/components/dashboard/UnitDetails/UnitInclusions';
import LeaseTerms from '@/components/dashboard/UnitDetails/LeaseTerms';
import Rent from '@/components/dashboard/UnitDetails/Rent';

const baseClass = 'UnitDetailsPage';

function UnitDetailsPage() {
  const { propertyId, unitId } = useParams<{
    propertyId: string;
    unitId: string;
  }>();
  const navigate = useNavigate();
  const [data, setData] = useState<IUnit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const profile = useSelector(profileSelector);
  const numericId = Number(propertyId);
  const numericUnitId = Number(unitId);
  const isNumeric = !isNaN(numericId) && !isNaN(numericUnitId);
  const { showError } = useToast();

  const { getSectionState, toggleSection } = useCollapsibleSections(
    'unit-details-page',
    {
      overview: false,
      information: false,
      inclusions: false,
      leaseTerms: false,
      rent: false,
    },
  );

  const unit = data;
  const property = data?.property;

  useEffect(() => {
    if (!propertyId || !unitId) return;

    setIsLoading(true);
    setFetchError(null);
    getUnitRequest(numericId, numericUnitId)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        const errorData = getAxiosErrors(err);
        let messages: string[] = [];

        if (errorData.errorMessage && Array.isArray(errorData.errorMessage)) {
          messages = errorData.errorMessage;
        } else {
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              messages.push(...errorData[key]);
            } else if (typeof errorData[key] === 'string') {
              messages.push(errorData[key]);
            }
          }
        }
        const errorMessage =
          messages.length > 0
            ? messages.join(', ')
            : 'Unknown error occurred.';
        showError(`Failed to load unit data: ${errorMessage}`);
        setFetchError(`Failed to load unit data: ${errorMessage}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [numericId, numericUnitId, propertyId, unitId, showError]);

  if (isLoading) {
    return (
      <Card className={baseClass}>
        <h4>Loading...</h4>
      </Card>
    );
  }

  if (!isNumeric) {
    return (
      <Card className={baseClass}>
        <h4>Invalid property or unit ID</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className={baseClass}>
        <h4>{fetchError}</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className={baseClass}>
        <h4>Unit data not found.</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  return (
    <div className={baseClass}>
      <Card.Header className={`${baseClass}--header`}>
        <span className={`${baseClass}--header-text`}>
          <span className={`${baseClass}--header-text-prop`}>
            Unit: {unit?.number}
          </span>
        </span>
        <div className={`${baseClass}--header-btns`}>
          <Button
            className={`${baseClass}--header-btn`}
            theme={'secondary'}
            onClick={() => {
              navigate(`/dashboard/properties/${propertyId}`);
            }}
          >
            Back to property
          </Button>
          <Button
            className={`${baseClass}--header-btn`}
            theme={'primary'}
            onClick={() => {
              navigate(
                `/dashboard/properties/${propertyId}/units/${unitId}/edit`,
              );
            }}
          >
            Edit
          </Button>
        </div>
      </Card.Header>

      {property && (
        <PropertyOverview
          property={property}
          loading={isLoading}
          collapsed={getSectionState('overview')}
          onToggleCollapse={() => toggleSection('overview')}
          showMap={true}
        />
      )}

      <UnitInformation
        unit={unit}
        collapsed={getSectionState('information')}
        onToggleCollapse={() => toggleSection('information')}
      />

      <UnitInclusions
        unit={unit}
        collapsed={getSectionState('inclusions')}
        onToggleCollapse={() => toggleSection('inclusions')}
      />

      <LeaseTerms
        unit={unit}
        collapsed={getSectionState('leaseTerms')}
        onToggleCollapse={() => toggleSection('leaseTerms')}
      />

      <Rent
        unit={unit}
        collapsed={getSectionState('rent')}
        onToggleCollapse={() => toggleSection('rent')}
      />

      <div className={`${baseClass}--bottom`}>
        <Button
          className={`${baseClass}--unit-btn`}
          onClick={() => {
            navigate(
              `/dashboard/properties/${propertyId}/units/${unitId}/edit`,
            );
          }}
        >
          <i className="fa-regular fa-pen-to-square"></i>
          Edit Unit
        </Button>
      </div>
    </div>
  );
}

export default UnitDetailsPage;
